import { useTranslation } from 'react-i18next';

import downwardIcon from '@/assets/icons/downwardIcon.svg';
import editIcon from '@/assets/icons/editIcon.svg';
import regenerateIcon from '@/assets/icons/regenerateIcon.svg';
import upwardIcon from '@/assets/icons/upwardIcon.svg';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface NarrativeExampleActionsProps {
  isEditing: boolean;
  isExpanded: boolean;
  isRegenerating: boolean;
  onCancel: () => void;
  onEditClick?: (() => void) | undefined;
  onRefreshClick?: (() => void) | undefined;
  onSave: () => void;
  onToggleExpanded: () => void;
}

export function NarrativeExampleActions({
  isEditing,
  isExpanded,
  isRegenerating,
  onCancel,
  onEditClick,
  onRefreshClick,
  onSave,
  onToggleExpanded,
}: NarrativeExampleActionsProps) {
  const { t } = useTranslation();

  if (isEditing) {
    return (
      <>
        <Button
          className="bg-primary hover:bg-primary-hover text-primary-foreground h-5 rounded-[4px] px-2 font-sans text-[10px] font-medium shadow-none"
          onClick={onSave}
        >
          {t('profile.example.save')}
        </Button>
        <Button
          className="border-border-subtle text-foreground hover:bg-back-bg2 bg-card h-5 rounded-[4px] border px-2 font-sans text-[10px] font-medium shadow-none"
          onClick={onCancel}
        >
          {t('profile.example.cancel')}
        </Button>
      </>
    );
  }

  const expandUi = isExpanded
    ? {
        alt: t('profile.example.collapseAlt'),
        aria: t('profile.example.collapseAria'),
        icon: downwardIcon,
      }
    : {
        alt: t('profile.example.expandAlt'),
        aria: t('profile.example.expandAria'),
        icon: upwardIcon,
      };

  return (
    <>
      {onEditClick && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={t('profile.example.editAria')}
              className="h-5 w-5 cursor-pointer p-0.5 transition-opacity hover:opacity-70"
              onClick={onEditClick}
              size="icon"
              variant="ghost"
            >
              <img
                alt={t('profile.example.editAlt')}
                className="icon-themed h-3.5 w-3.5"
                src={editIcon}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('profile.example.editLabel')}</p>
          </TooltipContent>
        </Tooltip>
      )}
      {onRefreshClick && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={t('profile.example.regenAria')}
              className="h-5 w-5 cursor-pointer p-0.5 transition-opacity hover:opacity-70 disabled:opacity-40"
              disabled={isRegenerating}
              onClick={onRefreshClick}
              size="icon"
              variant="ghost"
            >
              <img
                alt={t('profile.example.regenAlt')}
                className="icon-themed h-3.5 w-3.5"
                src={regenerateIcon}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('profile.example.regenLabel')}</p>
          </TooltipContent>
        </Tooltip>
      )}
      <Button
        aria-label={expandUi.aria}
        className="h-5 w-5 cursor-pointer p-0.5 transition-opacity hover:opacity-70"
        onClick={onToggleExpanded}
        size="icon"
        variant="ghost"
      >
        <img alt={expandUi.alt} className="icon-themed h-3.5 w-3.5" src={expandUi.icon} />
      </Button>
    </>
  );
}
